import React, { useState } from 'react';

import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from 'emotion-theming';
import { QueryClientProvider } from 'react-query';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ConfigProvider } from 'antd';
import { OpenFeatureProvider } from '@openfeature/react-sdk';

import FeedBackOverlay from 'components/module/FeedBackOverlay';
import SnackbarContainer from 'components/module/SnackbarContainer';
import { SnackbarProvider } from 'providers/snackbar';
import Routes from 'routes/index.routes';
import { store, persistor } from 'store';
import theme from 'utils/Theme';
import { queryClient } from 'utils/QueryClient';
import Toast from 'components/module/Toast';
import useNavigationSetup from 'hooks/useNavigationSetup';
import ErrorBoundaryWithLocation from 'components/module/ErrorBoundary';
import { OpenFeatureUserContextProvider } from 'providers/openFeatureUserContextProvider';

const App: React.FC = () => {
  useNavigationSetup();
  const [rehydrated, setRehydrated] = useState(false);

  const onRehydate = async () => {
    setRehydrated(true);
  };

  // listen service workers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register('/sw.js')
      .then(() => { })
      .catch(() => { });
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ReduxProvider store={store}>
        <PersistGate
          loading={null}
          persistor={persistor()}
          onBeforeLift={() => { }}
        >
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: theme.brand.secondary,
                colorError: theme.brand.errorStrong,
                colorSuccess: theme.brand.success,
              },
              components: {
                Card: {
                  paddingLG: 16,
                },
              },
            }}
          >
            <OpenFeatureProvider>
              <OpenFeatureUserContextProvider>
                <ThemeProvider theme={theme}>
                  <SnackbarProvider>
                    <ErrorBoundaryWithLocation>
                      <Routes />
                    </ErrorBoundaryWithLocation>
                    <ToastContainer style={{ zIndex: 10000 }} />
                    <Toast />
                    <FeedBackOverlay />
                    <SnackbarContainer />
                  </SnackbarProvider>
                </ThemeProvider>
              </OpenFeatureUserContextProvider>
            </OpenFeatureProvider>
          </ConfigProvider>
        </PersistGate>
      </ReduxProvider>
    </QueryClientProvider>
  );
};

export default App;
